import { type FC, useCallback, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import type { InjectedFormProps } from "redux-form";
import { Card, CardRowSimple, InfoToggletip } from "widgets";

import { ProjectColoredLink } from "@/modules/projects/components/ProjectColoredWidgets.js";
import { FormWarning } from "@/ui-modules/core/form/layout/FormWarning.tsx";

import { createLockedSamplesWarning, getSampleRowsForReviewScreen } from "../../../app-utils/sample.js";
import {
  type Attribute,
  NfParamTypeEnum,
  type NfParamValue,
  type NfPlaybookParam,
  type Organism,
  type Region,
} from "../../../graphql.js";
import type { IEditable } from "../../../redux/modules/data-editing.js";
import { type INFPlaybookExecutionState, submitNfPlaybook } from "../../../redux/modules/nf-playbook-execution.js";
import { getOrderedListOfNfTrackerAttributes } from "../../../redux/modules/playbook-execution.js";
import type { IAttributeLookup, ISampleEditingState } from "../../../redux/modules/sample-editing.js";
import type { IAppState } from "../../../redux/reducer.js";
import type { ReduxDispatch } from "../../../redux/redux-utils.js";
import { PLAYBOOK_TYPE } from "../../../settings/settings.js";
import InMemoryTableWithSelectionModel from "../../core/grid/InMemoryTableWithSelectionModel.js";
import PlaybookWizardStep, {
  type IPlaybookWizardStepInjectedProps,
  type IPlaybookWizardStepProvidedProps,
} from "../PlaybookWizardStep.js";

type INfPlaybookReviewScreenProps = IPlaybookWizardStepProvidedProps &
  InjectedFormProps<FormData, any> &
  IPlaybookWizardStepInjectedProps;

const NfPlaybookReviewScreen: FC<INfPlaybookReviewScreenProps> = (props) => {
  const dispatch = useDispatch<ReduxDispatch>();

  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>(null);

  const playbookExecutionState = useSelector<IAppState, INFPlaybookExecutionState>(
    (state) => state.nfPlaybookExecution,
  );
  const sampleEditingState = useSelector<IAppState, ISampleEditingState>((state) => state.sampleEditingState);
  const workflowValues = playbookExecutionState.workflowValues;
  const playbook = playbookExecutionState.playbook; // DIFF !!!!!!!!!
  const { eventName, eventDescription, project } = playbookExecutionState;
  const workflowParams = playbookExecutionState.playbook.nfConfig.workflowParameters;

  const createRowData = useCallback(() => {
    const data: IEditable[] = sampleEditingState.editingGrid;
    const attributeLookup: IAttributeLookup = sampleEditingState.attributeLookup;
    return getSampleRowsForReviewScreen(data, attributeLookup);
  }, []);

  const getColumnDefs = useCallback(() => {
    const orderedListOfAttributes: Attribute[] = getOrderedListOfNfTrackerAttributes(
      playbook.nfConfig.trackerParameters.sampleAttributes,
      sampleEditingState.sampleAttributes,
    );

    return orderedListOfAttributes.map((attribute: Attribute) => ({
      ...attribute.tableColumn,
      fixed: false, // so that the sample name is not back to first position, we respect the attributes ordering configuration
      groupName: attribute.groupName,
    }));
  }, []);

  const handleSubmitData = useCallback(() => {
    setErrorMessage(null);
    setIsSubmitting(true);
    return dispatch(submitNfPlaybook())
      .then(() => {
        setIsSubmitting(false);
        props.proceedWithNextStep();
      })
      .catch((e) => {
        setErrorMessage(e.message);
        setIsSubmitting(false);
      });
  }, []);

  const lockedSamples: IEditable[] = sampleEditingState.editingGrid.filter((r) => r.locked);
  const lockedSamplesWarning: string | null = createLockedSamplesWarning(lockedSamples?.length);

  return (
    <PlaybookWizardStep
      {...props}
      wizardFooterData={{
        error: errorMessage,
        enableSubmissionUponWizardError: true,
        warning: null,
        nextButtonProps: {
          label: "Submit",
          isLoading: isSubmitting,
          onPress: handleSubmitData,
        },
      }}
    >
      <div style={{ display: "flex", flexDirection: "column", overflow: "auto" }}>
        <div style={{ marginBottom: "20px" }}>
          <Card key="basic-info-card" title={"Submission Information"}>
            <CardRowSimple
              label={"Project"}
              value={<ProjectColoredLink project={project} noLink />}
              key={`parent-row-project`}
            />

            {eventName != null && <CardRowSimple label={"Event name"} value={eventName} key={`parent-row-name`} />}
            {eventDescription != null && (
              <CardRowSimple label={"Event description"} value={eventDescription} key={`parent-row-description`} />
            )}
          </Card>
        </div>
        <div style={{ marginBottom: "20px" }}>
          <Card key="workflow-info-card" title={"Parameters"}>
            {Object.keys(workflowValues).map((p: string) => {
              const workflowParam = workflowParams.find((w) => w.identifier === p);
              const values = workflowValues[p];
              const { displayValue, tooltip } = getWorkflowParamValueForDisplay(workflowParam, values);
              return (
                displayValue && (
                  <CardRowSimple label={workflowParam.label} key={`workflow-row-${p}`}>
                    <span>
                      {displayValue}
                      {tooltip && <InfoToggletip content={tooltip} />}
                    </span>
                  </CardRowSimple>
                )
              );
            })}
          </Card>
        </div>

        {lockedSamplesWarning && (
          <div style={{ marginBottom: 20 }}>
            <FormWarning>{lockedSamplesWarning}</FormWarning>
          </div>
        )}
        {playbook.type.id === PLAYBOOK_TYPE.NEXTFLOW_ANALYSIS_WITH_REGISTERED_SAMPLES && (
          <InMemoryTableWithSelectionModel
            columns={getColumnDefs()}
            rowGridData={createRowData()}
            uniqueGridIdentifier={"sample-registration-review"}
            useSelectionModel={false}
            editingTheme
            gridProps={{ domLayout: "autoHeight" }}
            showLockedColumn={true}
          />
        )}
      </div>
    </PlaybookWizardStep>
  );
};

function getWorkflowParamValueForDisplay(
  workflowParam: NfPlaybookParam,
  workflowParamValue: NfParamValue[],
): { displayValue: React.ReactNode; tooltip?: string } {
  if (!workflowParamValue?.length) {
    return { displayValue: undefined };
  }
  switch (workflowParam.type.type) {
    case NfParamTypeEnum.Region: {
      const regionValue: Region = workflowParamValue[0].region;
      return {
        displayValue: `${regionValue.contig.sequenceId} (${regionValue.range.start}-${regionValue.range.stop})`,
      };
    }
    case NfParamTypeEnum.OutputDir:
    case NfParamTypeEnum.File: {
      return {
        displayValue: workflowParamValue.map((paramValue) => paramValue.file.fileName).join(", "),
        tooltip: workflowParamValue.map((paramValue) => paramValue.file.humanReadablePath).join(", "),
      };
    }
    case NfParamTypeEnum.Boolean: {
      const value = workflowParamValue[0].booleanValue;
      return {
        displayValue: value != null ? (value ? "true" : "false") : value,
      };
    }
    case NfParamTypeEnum.Number: {
      return {
        displayValue: workflowParamValue[0].numberValue,
      };
    }
    case NfParamTypeEnum.Genome: {
      if (workflowParamValue[0].organism) {
        // it's a selected genome and/or sequences
        const genome: Organism = workflowParamValue[0].organism;
        return {
          displayValue: `${genome ? `${genome.name}${genome.species && ` (${genome.species.name})`}` : ""}`,
        };
      }
      // its an (array of) paths
      return {
        displayValue: workflowParamValue.map((paramValue) => paramValue.file?.fileName).join(", "),
        tooltip: workflowParamValue.map((paramValue) => paramValue.file?.humanReadablePath).join(", "),
      };
    }
    default: {
      const value = workflowParamValue[0].textValue;
      return {
        displayValue: value,
      };
    }
  }
}

export default NfPlaybookReviewScreen;
